// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — what the globe is showing: the dataset's values, or their change
 * from a held baseline (`docs/AYNI_XR_RESEARCH_TIER_PLAN.md`, slice 2).
 *
 * One small shared state so every reader agrees (owner, 2026-10-05: "if
 * it is off they read the dataset's values, if change is selected they
 * read the change in the dataset's units, and if % change is selected
 * then the percentage is read"): the Analyze panel sets it, the globe
 * draws it, and the pointer readout, the pins, their graph and their CSV
 * read through {@link viewReading} / {@link viewPin}.
 *
 * The colour range is fixed once per mode and baseline (`limit`), so one
 * colour means one amount in every frame, paused or playing. Pins keep
 * the values they read; the change is applied when they are shown, as a
 * shift (or a scale) by the baseline's value at the pin, so switching
 * modes never throws a filled graph away.
 */

import { latLonToTexelUv, type ProbeReading } from '../datasetProbe'
import type { ColorScale } from '../../types/color-scale'
import type { DatasetOverlayOptions } from '../../types'
import type { MeasurementSeries } from '../measure/series'
import { CHANGE_PALETTE_IDS, DEFAULT_CHANGE_COLOURS, symmetricRange, validRange, valueTable, type ChangeColours, type ChangeMode, type ChangeRange } from './changeMap'
import { climatologyAt, type Climatology } from './climatology'

/** How the change map follows playback: drawn by the graphics card every frame, or computed when playback stops. */
export type ChangeUpdate = 'live' | 'pause'

/**
 * AYNI: a period held as the baseline (statistics S2, `climatology.ts`):
 * the mean and σ of each calendar month, so the reference depends on the
 * time read: the frame on the globe for the pointer, each slot's own frame
 * for a pin's series.
 */
export interface PeriodReference {
  readonly clim: Climatology
  /** "1991–2020", for the legend and the CSV. */
  readonly label: string
  /** The group (calendar month, month and hour, or 0) of the frame at an instant (by its index on the axis); null when it cannot be told. */
  groupAt(timeMs: number): number | null
  /** The group of the frame on the globe now; null when it cannot be told. */
  groupNow(): number | null
  /**
   * A thinned climatology (`clim.factor` > 1): the frame on the globe read
   * at the texel the place's cell was read at, so the pointer compares like
   * with like; null without data there.
   */
  valueAtCell?(lat: number, lon: number): number | null
}

/** The held baseline's whole frame: its codes and where they sit on the Earth. */
export interface BaselineField {
  /** The held frame's codes; empty for a period (`period`). */
  readonly data: Uint8Array
  readonly width: number
  readonly height: number
  readonly scale: ColorScale
  readonly options?: DatasetOverlayOptions
  /** `baseline.gridKey`: frames with the same key pair texel by texel. */
  readonly gridKey: string
  /** The frame's time as the panel shows it (a period: "1991–2020 climatology"). */
  readonly frameLabel: string | null
  readonly units?: string
  /** AYNI: a period's climatology instead of one frame. */
  readonly period?: PeriodReference
}

/**
 * AYNI: another dataset read live as the reference (research tier,
 * "Compare", 2026-10-06): the real-time overlay B, so readers show A − B.
 * Unlike a held baseline it changes every frame, so a pin's series takes
 * B's value at each of its own frames (`seriesAt`), not one number.
 */
export interface CompareReference {
  /** For the legend's title: "A − B (units)". */
  readonly legendTitle: string
  /** B's title, for the pins' CSV. */
  readonly label: string
  /** B in A's units at a place on the frame now; null without data there. */
  valueAt(lat: number, lon: number): number | null
  /** B in A's units at a pin, on the pin's own time slots; null when none has been read. */
  seriesAt(pinId: string): MeasurementSeries | null
  /** B's own title (English, from the catalogue), for the figure's legend. */
  readonly title?: string
  /**
   * The difference as drawn, painted on demand (a global equirectangular
   * picture, north up): the figure needs it when the graphics card draws the
   * live difference and there is no picture layer.
   */
  picture?(): HTMLCanvasElement | null
}

export interface ChangeViewState {
  /** null: the dataset's own values. */
  readonly mode: ChangeMode | null
  readonly field: BaselineField | null
  /** AYNI: another dataset as the reference instead of a held frame ("Compare"); null for the baseline. */
  readonly other: CompareReference | null
  /** The fixed colour range (± limit), set from the first frame drawn in this mode; null until then. */
  readonly limit: number | null
  /**
   * AYNI (owner, 2026-10-20): the viewer's own colour range (Analyze →
   * Baseline), min < 0 < max; null for ±limit. Dropped with the limit, since
   * a new mode or baseline reads in other units.
   */
  readonly range: ChangeRange | null
  /**
   * AYNI (owner, 2026-10-20): the change map's palette and its direction,
   * for Baseline and Compare alike. Kept across modes and baselines, and
   * remembered on this device.
   */
  readonly colours: ChangeColours
  /** % change: the |baseline| under which no percentage is shown. */
  readonly floor: number | null
  readonly update: ChangeUpdate
  /** True while the change is on the globe (the panel that explains it is open). Readers follow only then. */
  readonly shown: boolean
}

const UPDATE_KEY = 'ayni-change-update'
const COLOURS_KEY = 'ayni-change-colours'

function storedColours(): ChangeColours {
  try {
    const raw = JSON.parse(globalThis.localStorage?.getItem(COLOURS_KEY) ?? 'null') as Partial<ChangeColours> | null
    if (raw && typeof raw.palette === 'string' && CHANGE_PALETTE_IDS.includes(raw.palette)) return { palette: raw.palette, inverted: raw.inverted === true }
  } catch {
    // Nothing kept, or a private window.
  }
  return DEFAULT_CHANGE_COLOURS
}

function storedUpdate(): ChangeUpdate {
  try {
    return globalThis.localStorage?.getItem(UPDATE_KEY) === 'pause' ? 'pause' : 'live'
  } catch {
    return 'live'
  }
}

let state: ChangeViewState = { mode: null, field: null, other: null, limit: null, range: null, colours: storedColours(), floor: null, update: storedUpdate(), shown: false }
const listeners = new Set<(s: ChangeViewState) => void>()
let fieldValues: { field: BaselineField; table: Float64Array } | null = null

export function changeViewState(): ChangeViewState {
  return state
}

/** Change the state; a new mode or baseline drops the fixed range, so it is set again from the next frame. */
export function setChangeView(patch: Partial<ChangeViewState>): void {
  const next = { ...state, ...patch }
  if (('mode' in patch && patch.mode !== state.mode) || ('field' in patch && patch.field !== state.field) || ('other' in patch && patch.other !== state.other)) {
    if (!('limit' in patch)) next.limit = null
    if (!('range' in patch)) next.range = null
    if (!('floor' in patch)) next.floor = null
  }
  if (next.mode === null || (next.field === null && next.other === null)) next.shown = false
  if (patch.update && patch.update !== state.update) {
    try {
      globalThis.localStorage?.setItem(UPDATE_KEY, patch.update)
    } catch {
      // A private window: the choice lasts this visit.
    }
  }
  if (patch.colours && patch.colours !== state.colours) {
    try {
      globalThis.localStorage?.setItem(COLOURS_KEY, JSON.stringify(patch.colours))
    } catch {
      // A private window: the choice lasts this visit.
    }
  }
  const changed = (Object.keys(next) as (keyof ChangeViewState)[]).some((k) => next[k] !== state[k])
  state = next
  if (changed) for (const fn of listeners) fn(state)
}

export function onChangeView(fn: (s: ChangeViewState) => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

/** AYNI: the colour range the change is painted in: the viewer's, else ±limit; null before the first frame. */
export function shownRange(s: ChangeViewState = state): ChangeRange | null {
  if (validRange(s.range)) return s.range
  return s.limit !== null ? symmetricRange(s.limit) : null
}

/** True when readers should show the change rather than the value. */
export function changeShown(s: ChangeViewState = state): boolean {
  return s.shown && s.mode !== null && (s.field !== null || s.other !== null)
}

/** A reference: the value to compare with, and its σ where a period gives one. */
export interface Reference {
  readonly mean: number | null
  readonly sigma: number | null
}

/**
 * The reference at a place: the other dataset when comparing, else the held
 * baseline; for a period, the climatology of the group at `timeMs` (the
 * frame on the globe when it is not given).
 */
function referenceAt(lat: number, lon: number, s: ChangeViewState, timeMs?: number): Reference {
  if (s.other) return { mean: s.other.valueAt(lat, lon), sigma: null }
  return baselineReferenceAt(lat, lon, s.field, timeMs)
}

/** The texel of a field's grid at a place; null outside it. */
function fieldTexel(lat: number, lon: number, field: BaselineField): { x: number; y: number } | null {
  const uv = latLonToTexelUv(lat, lon, field.options)
  if (!uv) return null
  return {
    x: Math.min(field.width - 1, Math.max(0, Math.floor(uv.u * field.width))),
    y: Math.min(field.height - 1, Math.max(0, Math.floor(uv.v * field.height))),
  }
}

/** AYNI: the held baseline's mean and σ at a place (σ only for a period); nulls outside its data. */
export function baselineReferenceAt(lat: number, lon: number, field: BaselineField | null = state.field, timeMs?: number): Reference {
  if (!field) return { mean: null, sigma: null }
  const at = fieldTexel(lat, lon, field)
  if (!at) return { mean: null, sigma: null }
  if (field.period) {
    const group = timeMs !== undefined ? field.period.groupAt(timeMs) : field.period.groupNow()
    if (group === null) return { mean: null, sigma: null }
    const { mean, sigma } = climatologyAt(field.period.clim, at.x, at.y, group)
    return { mean: Number.isFinite(mean) ? mean : null, sigma: Number.isFinite(sigma) ? sigma : null }
  }
  if (fieldValues?.field !== field) fieldValues = { field, table: valueTable(field.scale) }
  const v = fieldValues.table[field.data[at.y * field.width + at.x]]
  return { mean: Number.isFinite(v) ? v : null, sigma: null }
}

/** The held baseline's value at a place (a period: its mean, for the frame on the globe); null outside its data. */
export function baselineValueAt(lat: number, lon: number, field: BaselineField | null = state.field, timeMs?: number): number | null {
  return baselineReferenceAt(lat, lon, field, timeMs).mean
}

/**
 * A value read as the chosen change from `base`; null where there is none
 * (no baseline there, too near zero for %, or no σ for a standardised change).
 */
export function changeOf(value: number, base: number | null, mode: ChangeMode, floor: number | null, sigma: number | null = null): number | null {
  if (base === null || !Number.isFinite(value)) return null
  if (mode === 'difference') return value - base
  if (mode === 'sigma') return sigma !== null && sigma > 0 ? (value - base) / sigma : null
  if (Math.abs(base) < (floor ?? 0) || base === 0) return null
  return (100 * (value - base)) / Math.abs(base)
}

/** A % change left out because the baseline here is within the floor of zero (not missing data). */
export function nearZeroBase(base: number | null, mode: ChangeMode, floor: number | null): boolean {
  return mode === 'percent' && base !== null && (Math.abs(base) < (floor ?? 0) || base === 0)
}

/** The units a change is shown in: %, σ, or the data's. */
export function changeUnits(mode: ChangeMode, units: string | undefined): string | undefined {
  return mode === 'percent' ? '%' : mode === 'sigma' ? 'σ' : units
}

/** The units a reading is shown in. */
export function viewUnits(units: string | undefined, s: ChangeViewState = state): string | undefined {
  if (!changeShown(s)) return units
  return changeUnits(s.mode!, units)
}

/**
 * A pointer reading as the globe shows it: the value, or its change from
 * the baseline at that place. A place without a comparison reads as no
 * data, the way the map paints it grey.
 */
export function viewReading(reading: ProbeReading | null, lat: number, lon: number, s: ChangeViewState = state): ProbeReading | null {
  if (!reading || !changeShown(s) || reading.noData) return reading
  const ref = referenceAt(lat, lon, s)
  const period = s.other ? undefined : s.field?.period
  const value = period && period.clim.factor > 1 && period.valueAtCell ? period.valueAtCell(lat, lon) : reading.value
  if (value === null) return { ...reading, noData: true, labels: undefined, uncertainty: undefined }
  const change = changeOf(value, ref.mean, s.mode!, s.floor, ref.sigma)
  if (change === null) return { ...reading, noData: true, nearZero: nearZeroBase(ref.mean, s.mode!, s.floor) || undefined, labels: undefined, uncertainty: undefined }
  return {
    ...reading,
    value: change,
    units: changeUnits(s.mode!, reading.units),
    // A step in the data is a step in the change; a percentage or a σ has none.
    quantisationStep: s.mode === 'difference' ? reading.quantisationStep : undefined,
    labels: undefined,
    // AYNI: a single value's ± (errorBudget.ts) is not the ± of a difference; the step bounds it instead.
    uncertainty: undefined,
  }
}

/** A series shown as change from one baseline value (each slot moved; unread slots stay unread). */
export function viewSeries(series: MeasurementSeries, base: number | null, mode: ChangeMode, floor: number | null, sigma: number | null = null): MeasurementSeries {
  return {
    ...series,
    slots: series.slots.map((slot) => (slot.sampled && slot.value !== null
      ? { ...slot, value: changeOf(slot.value, base, mode, floor, sigma) }
      : slot)),
  }
}

/**
 * AYNI: a series against a period's climatology, slot by slot: each slot
 * against the mean (and σ) of the calendar month of the frame it read
 * (`SeriesSlot.timeMs`, else the slot's place on the axis), so a monthly
 * series becomes its anomalies.
 */
export function viewSeriesPeriod(
  series: MeasurementSeries,
  reference: (timeMs: number) => Reference,
  mode: ChangeMode,
  floor: number | null,
): MeasurementSeries {
  const last = series.slots.length - 1
  return {
    ...series,
    slots: series.slots.map((slot, i) => {
      if (!slot.sampled || slot.value === null) return slot
      const at = slot.timeMs ?? (last > 0 ? series.startMs + ((series.endMs - series.startMs) * i) / last : series.startMs)
      const ref = reference(at)
      return { ...slot, value: changeOf(slot.value, ref.mean, mode, floor, ref.sigma) }
    }),
  }
}

/**
 * A series against another series slot by slot (Compare): a slot B has not
 * read yet is shown unread, never against a guess.
 */
export function viewSeriesAgainst(series: MeasurementSeries, other: MeasurementSeries | null, mode: ChangeMode, floor: number | null): MeasurementSeries {
  return {
    ...series,
    slots: series.slots.map((slot, i) => {
      if (!slot.sampled || slot.value === null) return slot
      const b = other && other.slots.length === series.slots.length ? other.slots[i] : null
      if (!b || !b.sampled) return { sampled: false, value: null }
      return { ...slot, value: b.value === null ? null : changeOf(slot.value, b.value, mode, floor) }
    }),
  }
}

/** A pin as it is shown: its reading and series as change from the baseline at the pin, when the change is shown. */
export function viewPin<P extends { id?: string; lat: number; lon: number; reading: { value: number; noData: boolean } | null; series: MeasurementSeries | null }>(
  pin: P,
  s: ChangeViewState = state,
): P {
  if (!changeShown(s)) return pin
  const ref = referenceAt(pin.lat, pin.lon, s)
  const reading = pin.reading
    ? (() => {
        if (pin.reading!.noData) return pin.reading
        const v = changeOf(pin.reading!.value, ref.mean, s.mode!, s.floor, ref.sigma)
        return v === null
          ? { ...pin.reading!, noData: true, nearZero: nearZeroBase(ref.mean, s.mode!, s.floor) || undefined }
          : { ...pin.reading!, value: v, units: changeUnits(s.mode!, (pin.reading as { units?: string }).units) }
      })()
    : null
  if (s.other) {
    // B moves every frame: each slot against B's value at that slot.
    const other = pin.id ? s.other.seriesAt(pin.id) : null
    return { ...pin, reading, series: pin.series ? viewSeriesAgainst(pin.series, other, s.mode!, s.floor) : null }
  }
  // AYNI (owner, 2026-10-18): a pin following an extreme read each slot at
  // another place (`SeriesSlot.at`): each slot against the baseline there.
  const moving = !!pin.series?.slots.some((slot) => slot.at)
  if (s.field?.period) {
    // A climatology: each slot against its own calendar month.
    const field = s.field
    if (moving) return { ...pin, reading, series: viewSeriesAtSlots(pin.series!, pin, (where, at) => baselineReferenceAt(where.lat, where.lon, field, at), s) }
    const series = pin.series ? viewSeriesPeriod(pin.series, (at) => baselineReferenceAt(pin.lat, pin.lon, field, at), s.mode!, s.floor) : null
    return { ...pin, reading, series }
  }
  // As `viewSeries`: one baseline's change, without its sigma.
  if (moving) return { ...pin, reading, series: viewSeriesAtSlots(pin.series!, pin, (where) => ({ ...referenceAt(where.lat, where.lon, s), sigma: null }), s) }
  return { ...pin, reading, series: pin.series ? viewSeries(pin.series, ref.mean, s.mode!, s.floor) : null }
}

/** AYNI: each slot against the reference where it was read (else where the pin is), at its frame's time. */
function viewSeriesAtSlots(
  series: MeasurementSeries,
  pin: { lat: number; lon: number },
  reference: (where: { lat: number; lon: number }, timeMs: number) => Reference,
  s: ChangeViewState,
): MeasurementSeries {
  const last = series.slots.length - 1
  return {
    ...series,
    slots: series.slots.map((slot, i) => {
      if (!slot.sampled || slot.value === null) return slot
      const at = slot.timeMs ?? (last > 0 ? series.startMs + ((series.endMs - series.startMs) * i) / last : series.startMs)
      const ref = reference(slot.at ?? pin, at)
      return { ...slot, value: changeOf(slot.value, ref.mean, s.mode!, s.floor, ref.sigma) }
    }),
  }
}

/** For tests: back to the start. */
export function resetChangeView(): void {
  state = { mode: null, field: null, other: null, limit: null, range: null, colours: storedColours(), floor: null, update: storedUpdate(), shown: false }
  fieldValues = null
  listeners.clear()
}
